import type {
  CourseRecord,
  DashboardSnapshotResponse,
  CurrentUserResponse,
  MatchDayAttendanceRecord,
  InviteLookupResponse,
  InviteRecord,
  LeaguePortalStatusRecord,
  LeagueMemberRecord,
  LeagueRecord,
  MatchDayRecord,
  MatchRecord,
  PlayerRecord,
  PublishedPortalSnapshotRecord,
  PlayerSeasonProfileHistoryResponse,
  ScoreRecord,
  SeasonPlayerRecord,
  SeasonRecord,
  StandaloneRoundRecord,
  StandingRecord,
  SupplementalScorecardRecord,
} from './types.js';

export type QaCleanupScope = 'all_scores' | 'all_courses' | 'all_seasons' |
  'all_season_players' | 'all_league_players' | 'entire_league';

export interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

export interface ErrorEnvelope {
  success: false;
  error: {
    code: string;
    message: string;
    details?: string;
  };
}

type ApiEnvelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => Promise<string> | string;
}

export class ApiClient {
  private readonly baseUrl: string;

  constructor(private readonly options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.endsWith('/')
      ? options.baseUrl.slice(0, -1)
      : options.baseUrl;
  }

  private buildUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}`;
  }

  private async parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    if (!contentType.includes('application/json')) {
      const body = (await response.text().catch(() => '')).trim();
      const preview = body.slice(0, 120);
      throw new ApiClientError(
        response.status || 500,
        'INVALID_API_RESPONSE',
        `Expected JSON API response but received "${contentType || 'unknown'}". Verify VITE_API_BASE_URL points to the API base.`,
        preview || undefined,
      );
    }

    try {
      return (await response.json()) as ApiEnvelope<T>;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON payload';
      throw new ApiClientError(
        response.status || 500,
        'INVALID_API_RESPONSE',
        `Failed to parse JSON API response: ${message}. Verify VITE_API_BASE_URL points to the API base.`,
      );
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers: Record<string, string> = {},
  ): Promise<T> {
    const token = await this.options.getToken();
    const response = await fetch(this.buildUrl(path), {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    const payload = await this.parseEnvelope<T>(response);
    if (!response.ok || payload.success === false) {
      if (payload.success === false) {
        throw new ApiClientError(response.status, payload.error.code, payload.error.message, payload.error.details);
      }
      throw new ApiClientError(response.status, 'INTERNAL_ERROR', 'Unexpected API response');
    }

    return payload.data;
  }

  getCurrentUser(): Promise<CurrentUserResponse> {
    return this.request('GET', '/user/me');
  }

  linkPlayer(payload: { email?: string; name?: string }): Promise<{ player: PlayerRecord; created: boolean }> {
    return this.request('POST', '/user/link-player', payload);
  }

  getLeagues(): Promise<{ leagues: LeagueRecord[]; canCreateLeague: boolean }> {
    return this.request('GET', '/leagues');
  }

  getLeague(leagueId: string): Promise<{ league: LeagueRecord }> {
    return this.request('GET', `/leagues/${leagueId}`);
  }

  cleanupQaLeague(leagueId: string, payload: { scope: QaCleanupScope; confirmLeagueName: string }): Promise<{
    scope: QaCleanupScope; deleted: Record<string, number>;
  }> {
    return this.request('POST', `/leagues/${leagueId}/qa-cleanup`, payload);
  }

  getDashboardSnapshot(leagueId: string, seasonId?: string): Promise<DashboardSnapshotResponse> {
    const query = seasonId ? `?season_id=${seasonId}` : '';
    return this.request('GET', `/leagues/${leagueId}/dashboard-snapshot${query}`);
  }

  getLeaguePortalStatus(leagueId: string): Promise<{ portal: LeaguePortalStatusRecord }> {
    return this.request('GET', `/leagues/${leagueId}/portal`);
  }

  getLeaguePortalSnapshot(leagueId: string): Promise<{ snapshot: PublishedPortalSnapshotRecord }> {
    return this.request('GET', `/leagues/${leagueId}/portal/snapshot`);
  }

  updateLeaguePortalPassword(
    leagueId: string,
    payload: { password: string },
  ): Promise<{ portal: LeaguePortalStatusRecord }> {
    return this.request('PUT', `/leagues/${leagueId}/portal/password`, payload);
  }

  publishLeaguePortal(
    leagueId: string,
    payload: { seasonId: string },
  ): Promise<{ portal: LeaguePortalStatusRecord }> {
    return this.request('POST', `/leagues/${leagueId}/portal/publish`, payload);
  }

  unpublishLeaguePortal(leagueId: string): Promise<{ portal: LeaguePortalStatusRecord }> {
    return this.request('DELETE', `/leagues/${leagueId}/portal/publish`);
  }

  createLeague(payload: { name: string; description?: string; startingHandicap: number }): Promise<{ league: LeagueRecord }> {
    return this.request('POST', '/leagues', payload);
  }

  updateLeague(
    leagueId: string,
    payload: { name?: string; description?: string },
  ): Promise<{ league: LeagueRecord }> {
    return this.request('PUT', `/leagues/${leagueId}`, payload);
  }

  getStandings(leagueId: string, seasonId?: string): Promise<{ standings: StandingRecord[] }> {
    const query = seasonId ? `?season_id=${seasonId}` : '';
    return this.request('GET', `/leagues/${leagueId}/standings${query}`);
  }

  getMatches(leagueId: string, status?: 'scheduled' | 'completed'): Promise<{ matches: MatchRecord[] }> {
    const query = status ? `?status=${status}` : '';
    return this.request('GET', `/leagues/${leagueId}/matches${query}`);
  }

  getMatch(leagueId: string, matchId: string): Promise<{ match: MatchRecord }> {
    return this.request('GET', `/leagues/${leagueId}/matches/${matchId}`);
  }

  getMatchScores(leagueId: string, matchId: string): Promise<{ scores: ScoreRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/matches/${matchId}/scores`);
  }

  getMembers(leagueId: string): Promise<{ members: LeagueMemberRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/members`);
  }

  addMember(
    leagueId: string,
    payload: {
      player_id?: string;
      email?: string;
      name?: string;
      role: 'admin' | 'player';
      startingHandicap?: number;
    },
  ): Promise<{ member: LeagueMemberRecord; player: PlayerRecord }> {
    return this.request('POST', `/leagues/${leagueId}/members`, payload);
  }

  updateMember(
    leagueId: string,
    playerId: string,
    payload: { role?: 'admin' | 'player'; startingHandicap?: number },
  ): Promise<{ member: LeagueMemberRecord }> {
    return this.request('PUT', `/leagues/${leagueId}/members/${playerId}`, payload);
  }

  removeMember(leagueId: string, playerId: string): Promise<{ removed: boolean }> {
    return this.request('DELETE', `/leagues/${leagueId}/members/${playerId}`);
  }

  getSeasons(leagueId: string): Promise<{ seasons: SeasonRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/seasons`);
  }

  getActiveSeason(leagueId: string): Promise<{ season: SeasonRecord }> {
    return this.request('GET', `/leagues/${leagueId}/seasons/active`);
  }

  createSeason(
    leagueId: string,
    payload: {
      name: string;
      startDate: string;
      endDate: string;
      active: boolean;
      description?: string;
    },
  ): Promise<{ season: SeasonRecord }> {
    return this.request('POST', `/leagues/${leagueId}/seasons`, payload);
  }

  updateSeason(
    leagueId: string,
    seasonId: string,
    payload: {
      name?: string;
      startDate?: string;
      endDate?: string;
      active?: boolean;
      description?: string;
    },
  ): Promise<{ season: SeasonRecord }> {
    return this.request('PUT', `/leagues/${leagueId}/seasons/${seasonId}`, payload);
  }

  getSeasonMatches(leagueId: string, seasonId: string): Promise<{ matches: MatchRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/seasons/${seasonId}/matches`);
  }

  getSeasonPlayers(leagueId: string, seasonId: string): Promise<{ seasonPlayers: SeasonPlayerRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/seasons/${seasonId}/players`);
  }

  addSeasonPlayer(
    leagueId: string,
    seasonId: string,
    payload: { playerId: string },
  ): Promise<{ seasonPlayer: SeasonPlayerRecord }> {
    return this.request('POST', `/leagues/${leagueId}/seasons/${seasonId}/players`, payload);
  }

  removeSeasonPlayer(leagueId: string, seasonId: string, playerId: string): Promise<{ removed: boolean }> {
    return this.request('DELETE', `/leagues/${leagueId}/seasons/${seasonId}/players/${playerId}`);
  }

  getCourses(leagueId: string): Promise<{ courses: CourseRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/courses`);
  }

  createCourse(
    leagueId: string,
    payload: {
      name: string;
      par: number;
      courseRating: number;
      slopeRating: number;
      holePars: number[];
      holeHandicaps: number[];
    },
  ): Promise<{ course: CourseRecord }> {
    return this.request('POST', `/leagues/${leagueId}/courses`, payload);
  }

  updateCourse(
    leagueId: string,
    courseId: string,
    payload: {
      name: string;
      par: number;
      courseRating: number;
      slopeRating: number;
      holePars: number[];
      holeHandicaps: number[];
    },
  ): Promise<{ course: CourseRecord }> {
    return this.request('PUT', `/leagues/${leagueId}/courses/${courseId}`, payload);
  }

  getMatchDays(leagueId: string, seasonId?: string): Promise<{ matchDays: MatchDayRecord[] }> {
    const query = seasonId ? `?season_id=${seasonId}` : '';
    return this.request('GET', `/leagues/${leagueId}/match-days${query}`);
  }

  createMatchDay(
    leagueId: string,
    payload: {
      date: string;
      seasonId: string;
      courseId: string | null;
      hammerHole?: number | null;
      isPlayoffWeek?: boolean;
      matches: unknown[];
    },
  ): Promise<{ matchDay: MatchDayRecord; matches: MatchRecord[] }> {
    return this.request('POST', `/leagues/${leagueId}/match-days`, payload);
  }

  getMatchDay(leagueId: string, matchDayId: string): Promise<{ matchDay: MatchDayRecord }> {
    return this.request('GET', `/leagues/${leagueId}/match-days/${matchDayId}`);
  }

  updateMatchDay(
    leagueId: string,
    matchDayId: string,
    payload: {
      date?: string;
      courseId?: string | null;
      hammerHole?: number | null;
      isPlayoffWeek?: boolean;
      status?: 'locked';
    },
  ): Promise<{ matchDay: MatchDayRecord }> {
    return this.request('PUT', `/leagues/${leagueId}/match-days/${matchDayId}`, payload);
  }

  deleteMatchDay(leagueId: string, matchDayId: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', `/leagues/${leagueId}/match-days/${matchDayId}`);
  }

  getMatchDayMatches(leagueId: string, matchDayId: string): Promise<{ matches: MatchRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/match-days/${matchDayId}/matches`);
  }

  replaceMatchDayMatches(
    leagueId: string,
    matchDayId: string,
    payload: { matches: Array<{ playerAId: string; playerBId: string }> },
  ): Promise<{ matches: MatchRecord[] }> {
    return this.request('PUT', `/leagues/${leagueId}/match-days/${matchDayId}/matches`, payload);
  }

  getMatchDayScores(
    leagueId: string,
    matchDayId: string,
  ): Promise<{ scores: ScoreRecord[]; supplementalCards: SupplementalScorecardRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/match-days/${matchDayId}/scores`);
  }

  getSeasonAttendance(
    leagueId: string,
    seasonId: string,
  ): Promise<{ attendance: MatchDayAttendanceRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/seasons/${seasonId}/attendance`);
  }

  updateMyMatchDayAttendance(
    leagueId: string,
    matchDayId: string,
    payload: { status: 'going' | 'tentative' | 'unavailable' },
  ): Promise<{ attendance: MatchDayAttendanceRecord }> {
    return this.request('PUT', `/leagues/${leagueId}/match-days/${matchDayId}/attendance/me`, payload);
  }

  submitMatchDayScores(
    leagueId: string,
    payload: {
      matchDayId: string;
      scores: Array<{ matchId: string; playerId: string; playerAbsent: boolean; holeScores?: number[] }>;
      supplementalCards?: Array<{
        matchId: string;
        forPlayerId: string;
        playerId: string;
        participantType: 'sub';
        holeScores: number[];
      }>;
      replaceSupplementalCards?: boolean;
    },
    idempotencyKey: string,
  ): Promise<{ matchDayId: string; processedMatches: MatchRecord[] }> {
    return this.request('POST', `/leagues/${leagueId}/match-days/scores`, payload, {
      'idempotency-key': idempotencyKey,
    });
  }

  getMatchDayStandaloneRounds(
    leagueId: string,
    matchDayId: string,
  ): Promise<{ rounds: StandaloneRoundRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/match-days/${matchDayId}/standalone-rounds`);
  }

  submitStandaloneRound(
    leagueId: string,
    matchDayId: string,
    payload: {
      playerId: string;
      playerAbsent?: boolean;
      holeScores?: number[];
      subPlayerId?: string;
      subHoleScores?: number[];
    },
  ): Promise<{ round: StandaloneRoundRecord | null }> {
    return this.request('POST', `/leagues/${leagueId}/match-days/${matchDayId}/standalone-rounds`, payload);
  }

  deleteStandaloneRound(leagueId: string, roundId: string): Promise<{ deleted: boolean }> {
    return this.request('DELETE', `/leagues/${leagueId}/standalone-rounds/${roundId}`);
  }

  getInvites(leagueId: string): Promise<{ invites: InviteRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/invites`);
  }

  createInvite(
    leagueId: string,
    payload: {
      startingHandicap?: number;
      scope?: 'league' | 'season';
      seasonId?: string;
      expiresInDays?: number;
      maxUses?: number;
      playerId?: string;
    },
  ): Promise<{ invite: InviteRecord }> {
    return this.request('POST', `/leagues/${leagueId}/invites`, payload);
  }

  revokeInvite(leagueId: string, inviteId: string): Promise<{ revoked: boolean }> {
    return this.request('DELETE', `/leagues/${leagueId}/invites/${inviteId}`);
  }

  getInviteByToken(
    token: string,
  ): Promise<InviteLookupResponse> {
    return this.request('GET', `/invites/${token}`);
  }

  acceptInvite(
    token: string,
    payload?: {
      email?: string;
      name?: string;
    },
  ): Promise<{ accepted: boolean; leagueId: string; seasonId?: string | null }> {
    return this.request('POST', `/invites/${token}/accept`, payload);
  }

  recalculateHandicaps(leagueId: string): Promise<{ seasonId: string | null; recalculated: Array<{ playerId: string; handicapIndex: number }> }> {
    return this.request('POST', `/leagues/${leagueId}/jobs/recalculate-handicaps`);
  }

  processMatch(leagueId: string, matchId: string): Promise<{ processed: boolean; match: MatchRecord; scores: ScoreRecord[] }> {
    return this.request('POST', `/leagues/${leagueId}/jobs/process-match/${matchId}`);
  }

  getPlayerScores(leagueId: string, playerId: string): Promise<{ scores: ScoreRecord[] }> {
    return this.request('GET', `/leagues/${leagueId}/players/${playerId}/scores`);
  }

  getPlayerSeasonProfileHistory(
    leagueId: string,
    seasonId: string,
    playerId: string,
  ): Promise<PlayerSeasonProfileHistoryResponse> {
    return this.request('GET', `/leagues/${leagueId}/seasons/${seasonId}/players/${playerId}/profile-history`);
  }

  getPlayerLeagueHandicap(
    leagueId: string,
    playerId: string,
  ): Promise<{ startingHandicap: number; currentHandicapIndex: number;
    rounds: Array<{ id: string; date: string; differential: number; appliedIndex: number; handicapIndex: number }> }> {
    return this.request('GET', `/leagues/${leagueId}/players/${playerId}/handicap`);
  }
}
